import React, {
  forwardRef,
  InputHTMLAttributes,
  MutableRefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { Check, Minus } from "lucide-react";
import styles from "./MGInputCheckbox.module.css";

export type TMGInputCheckboxBackground = "light" | "dark" | "brand";
export type TMGInputCheckboxShape = "round" | "square";
export type TMGInputCheckboxSize = "xs" | "sm";

export type TMGInputCheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "type"> & {
  background?: TMGInputCheckboxBackground;
  checkboxId?: string;
  checkboxSize?: TMGInputCheckboxSize;
  dataTestId?: string;
  error?: boolean;
  indeterminate?: boolean;
  shape?: TMGInputCheckboxShape;
};

let generatedId = 0;

export const MGInputCheckbox = forwardRef<HTMLInputElement, TMGInputCheckboxProps>(
  (
    {
      background = "light",
      checkboxId,
      checkboxSize = "sm",
      className,
      dataTestId,
      disabled = false,
      error = false,
      id,
      indeterminate = false,
      shape = "square",
      tabIndex,
      "aria-checked": ariaChecked,
      "aria-invalid": ariaInvalid,
      ...inputProps
    },
    forwardedRef,
  ) => {
    const [internalId] = useState(() => `mg-checkbox-${++generatedId}`);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const resolvedId = checkboxId || id || internalId;

    useEffect(() => {
      if (inputRef.current) inputRef.current.indeterminate = indeterminate;
    }, [indeterminate]);

    const setInputRef = (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) {
        (forwardedRef as MutableRefObject<HTMLInputElement | null>).current = node;
      }
    };

    return (
      <span
        className={`${styles.checkbox}${className ? ` ${className}` : ""}`}
        data-background={background}
        data-disabled={disabled}
        data-error={error}
        data-indeterminate={indeterminate}
        data-shape={shape}
        data-size={checkboxSize}
        data-testid={dataTestId}>
        <input
          {...inputProps}
          ref={setInputRef}
          className={styles.input}
          type="checkbox"
          id={resolvedId}
          disabled={disabled}
          tabIndex={disabled ? -1 : tabIndex}
          aria-checked={indeterminate ? "mixed" : ariaChecked}
          aria-invalid={ariaInvalid === undefined ? error || undefined : ariaInvalid}
        />
        <span className={styles.visual} aria-hidden="true">
          {indeterminate ? (
            <Minus className={styles.icon} strokeWidth={3} />
          ) : (
            <Check className={styles.icon} strokeWidth={3} />
          )}
        </span>
      </span>
    );
  },
);

MGInputCheckbox.displayName = "MGInputCheckbox";
